// "+ Create new vendor" (D107.1): the Vendors page's form in a popup, for any screen that picks
// a vendor. On save the new vendor is handed to `onCreated` (the picker selects it), then `onClose`
// is called, so a host that only flips `open` off in onClose needs nothing more. Whoever opens it
// decides who may see the button (vendors:create); the server enforces it.
//
// The form is loaded when the popup first opens, not with the screens that merely mention it:
// every vendor picker imports this file, and the form's phone-number library is only needed by
// the person who actually creates a vendor.
import React, { Suspense } from 'react';
import { Modal } from '../../components/Modal';
import type { VendorFormValues } from './VendorForm';

const VendorForm = React.lazy(() => import('./VendorForm'));

/**
 * A phone the OCR read off an invoice, down to the bare digits `VendorFormValues.phone` (and
 * `PhoneInput`) expect — never a second phone PARSER, just stripping the printed punctuation a
 * person would delete by hand before typing it into this same field. Vendors on this system are
 * Trinidad & Tobago only (`VendorForm`'s own `TRINIDAD_ONLY_COUNTRY`), so a 10-digit printed
 * number already carrying that local "868" prefix (e.g. "868 653 1196") would, left alone, be
 * typed into a box that adds its own "+1868" ahead of it — the one case worth a line for, not a
 * general area-code guesser. Anything else is handed through untouched for the existing phone
 * validation to catch, exactly as if the reviewer had typed it.
 */
function digitsForTrinidadPhoneField(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits.length === 10 && digits.startsWith('868') ? digits.slice(3) : digits;
}

export interface CreateVendorModalProps {
  open: boolean;
  /** Pre-fills the name (what was typed in the picker, or what an invoice printed). */
  initialName?: string;
  /** Pre-fills the phone, address and email the SAME invoice already had parsed (item 31, D86). */
  initialPhone?: string;
  initialAddress?: string;
  initialEmail?: string;
  onClose: () => void;
  onCreated: (vendor: { _id: string; name: string }) => void;
}

export const CreateVendorModal: React.FC<CreateVendorModalProps> = ({
  open,
  initialName,
  initialPhone,
  initialAddress,
  initialEmail,
  onClose,
  onCreated,
}) => {
  const initial: Partial<VendorFormValues> | undefined =
    initialName || initialPhone || initialAddress || initialEmail
      ? {
          ...(initialName ? { name: initialName } : {}),
          ...(initialPhone ? { phone: digitsForTrinidadPhoneField(initialPhone) } : {}),
          ...(initialAddress ? { address: initialAddress } : {}),
          ...(initialEmail ? { email: initialEmail } : {}),
        }
      : undefined;

  return (
    // Events inside a portal bubble through the React tree to whatever opened the popup: a click on
    // the popup's own background must not reach, say, the grid row the picker sits in. `contents`:
    // the wrapper takes no space where the picker is. Escape is a window listener, not affected.
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      <Modal isOpen={open} title="Add new vendor" onClose={onClose} overlayZClass="z-[90]">
        <Suspense fallback={<p className="text-sm text-slate-500">Loading…</p>}>
          <VendorForm
            initial={initial}
            onSaved={({ id, name }) => {
              if (id) onCreated({ _id: id, name });
              onClose();
            }}
            onCancel={onClose}
          />
        </Suspense>
      </Modal>
    </div>
  );
};

export default CreateVendorModal;
